import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Leaf } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useProducts, useProgrammes, useStories, useArticles, useListened } from "@/hooks/useCatalogue";
import ProductCard from "@/components/ProductCard";
import { BrandLogo } from "@/components/BrandLogo";
import HeroMedia from "@/components/media/HeroMedia";
import RippleAccent from "@/components/media/RippleAccent";
import Reveal from "@/components/Reveal";
import { useBrand } from "@/hooks/useCatalogue";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const dise = [
  { n: "I", title: "Discover", text: "Explore our products and the thinking behind each formulation." },
  { n: "II", title: "Experience", text: "Apply for an available programme when it opens, subject to eligibility." },
  { n: "III", title: "Share", text: "Tell us how a product fits into your everyday ritual—privately, or with the community." },
];

const faqs = [
  { q: "Is joining the community free?", a: "Yes. Joining Rituals With You is free. Individual programme costs, shipping terms and eligibility are configured and clearly disclosed before an application opens." },
  { q: "Does joining guarantee I receive a product?", a: "No. Membership does not guarantee a product. Selection depends on programme eligibility and availability." },
  { q: "Can I share critical feedback?", a: "Absolutely. We welcome honest experiences—positive and critical. Your private feedback stays private by default; sharing a public story is a separate, voluntary choice." },
  { q: "What are the three products?", a: "We launch with three: Serum, Sunscreen and Facewash, each with a dedicated page. Full formulation details will be shared soon." },
];

function SectionHeader({ eyebrow, title, className = "", light = false }) {
  return (
    <div className={className}>
      {eyebrow && <p className={`eyebrow mb-6 ${light ? "text-champagne" : ""}`}>{eyebrow}</p>}
      <h2 className={`font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-balance leading-[0.98] ${light ? "text-ivory" : "text-forest"}`}>
        {title}
      </h2>
    </div>
  );
}

export default function Home() {
  const { data: products = [] } = useProducts();
  const { data: programmes = [] } = useProgrammes();
  const { data: stories = [] } = useStories();
  const { data: articles = [] } = useArticles();
  const { data: listened = [] } = useListened();
  const { data: brand } = useBrand();
  const [activeSystem, setActiveSystem] = useState(null);
  const current = products.find((p) => p.id === activeSystem) || products[0];

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-forest text-ivory overflow-hidden grain" data-testid="hero-section">
        <BrandLogo
          variant="mark"
          tone="light"
          className="pointer-events-none select-none absolute -left-20 -bottom-40 w-[620px] opacity-[0.05]"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center pt-16 pb-24 sm:pt-24 sm:pb-32">
          <div className="lg:col-span-6 animate-fade-up">
            <p className="eyebrow text-champagne">{BRAND.descriptor}</p>
            <h1
              data-testid="hero-title"
              className="mt-8 font-serif font-light text-6xl sm:text-7xl lg:text-[104px] tracking-tight leading-[0.9] text-balance"
            >
              {BRAND.community}
            </h1>
            <p className="mt-6 font-serif italic text-2xl sm:text-3xl text-champagne">{BRAND.tagline}</p>
            <p className="mt-8 text-base sm:text-lg leading-relaxed text-ivory/70 max-w-md font-light">
              {BRAND.heroCopy}
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link to="/join" data-testid="hero-join-cta" className="btn btn-light">
                {BRAND.joinCta} <ArrowRight size={14} />
              </Link>
              <Link to="/products" data-testid="hero-explore-cta" className="btn btn-ghost-light">
                {BRAND.exploreCta}
              </Link>
            </div>
          </div>

          <HeroMedia
            media={brand?.hero_media}
            label={BRAND.participation}
            caption="One shared journey, one ritual at a time."
            className="lg:col-span-5 lg:col-start-8 animate-fade-up"
          />
        </div>

        <div className="relative border-t border-ivory/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-ivory/10">
            {dise.map((d) => (
              <div key={d.title} className="py-6 sm:px-8 first:pl-0 flex items-baseline gap-4">
                <span className="numeral">{d.n}</span>
                <span className="text-[11px] uppercase tracking-[0.26em] text-ivory/70">{d.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32" data-testid="products-section">
        <Reveal className="grid lg:grid-cols-12 gap-8 items-end mb-16">
          <SectionHeader eyebrow="Our Rituals" title="Three products, in order" className="lg:col-span-7" />
          <p className="lg:col-span-4 lg:col-start-9 text-sm text-charcoal/60 leading-relaxed font-light">
            Serum, Sunscreen and Facewash. This collection order is not a recommended application order.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              <ProductCard product={p} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* THE 3-WAY SYSTEM */}
      <section className="relative logo-gradient text-ivory overflow-hidden grain" data-testid="three-way-system-section">
        <RippleAccent className="absolute -right-40 -bottom-56 w-[820px] h-[820px] opacity-60" rings={6} testid="system-ripple-accent" />
        <RippleAccent className="absolute -left-64 -top-64 w-[620px] h-[620px] opacity-40" rings={4} testid="system-ripple-accent-2" />
        <Reveal className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionHeader eyebrow={BRAND.philosophy} title="The 3-Way System" light />
              <p className="mt-8 text-ivory/70 max-w-md font-light leading-relaxed">
                An architecture built around three considered pathways per product. Pathway details are being finalised with care.
              </p>
              <div className="mt-10 flex flex-wrap gap-2" data-testid="three-way-system-selector">
                {products.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setActiveSystem(p.id)}
                    className={`px-6 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                      current?.id === p.id ? "bg-ivory text-forest" : "border border-ivory/30 text-ivory/80 hover:border-ivory"
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-px bg-ivory/15 border border-ivory/15">
              {(current?.pathways || [1, 2, 3]).map((pw, i) => (
                <div key={i} data-testid={`system-pathway-tbc-${i + 1}`} className="bg-forest/30 backdrop-blur-sm p-8 min-h-[260px] flex flex-col justify-between">
                  <div className="flex items-center justify-between text-champagne">
                    <span className="numeral">{["I", "II", "III"][i]}</span>
                    <Leaf size={16} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] text-ivory/50">Pathway {i + 1}</p>
                    <p className="mt-3 font-serif text-3xl font-light">To be confirmed</p>
                    <p className="mt-2 text-sm text-ivory/55 font-light">Formulation details will be shared soon.</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* DISCOVER EXPERIENCE SHARE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <SectionHeader eyebrow="How participation works" title="Discover · Experience · Share" />
            <p className="mt-8 text-sm text-charcoal/55 font-light leading-relaxed">
              Selection depends on programme eligibility and availability. Membership does not guarantee a product.
            </p>
          </Reveal>
          <ol className="lg:col-span-7 lg:col-start-6 border-t border-forest/15">
            {dise.map((d, i) => (
              <Reveal as="li" key={d.title} delay={i * 0.14} className="grid grid-cols-[3rem_1fr] sm:grid-cols-[5rem_1fr_1fr] gap-6 py-10 border-b border-forest/15">
                <span className="numeral pt-2">{d.n}</span>
                <h3 className="font-serif text-3xl sm:text-4xl text-forest font-light">{d.title}</h3>
                <p className="col-span-2 sm:col-span-1 text-sm text-charcoal/70 leading-relaxed font-light pt-2">{d.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* CURRENT PROGRAMMES */}
      <section className="bg-linen">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <SectionHeader eyebrow="Experience" title="Current programmes" />
          {programmes.length === 0 ? (
            <div className="mt-14 border border-forest/20 p-1">
              <div className="border border-forest/10 px-8 py-16 sm:py-20 text-center">
                <BrandLogo variant="mark" className="h-14 w-auto mx-auto opacity-80" />
                <p className="mt-8 font-serif font-light text-3xl sm:text-4xl text-forest">No programmes are open right now</p>
                <p className="mt-4 text-charcoal/65 max-w-md mx-auto font-light leading-relaxed">
                  New experience programmes open from time to time. Join the community and we'll invite you when one that fits becomes available.
                </p>
                <Link to="/join" className="btn btn-primary mt-10">{BRAND.joinCta}</Link>
              </div>
            </div>
          ) : (
            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {programmes.map((pr) => (
                <Link key={pr.slug} to={`/programmes/${pr.slug}`} className="border border-forest/15 bg-cream p-8 hover:border-forest/50 transition-colors">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-champagne">{pr.status}</span>
                  <h3 className="mt-3 font-serif text-3xl text-forest font-light">{pr.title}</h3>
                  <p className="mt-3 text-sm text-charcoal/70 font-light">{pr.summary}</p>
                </Link>
              ))}
            </div>
          )}
        </Reveal>
      </section>

      {/* STORIES */}
      {stories.length > 0 && (
        <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <SectionHeader eyebrow="Member experiences" title="Stories from the community" />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {stories.slice(0, 3).map((s) => (
              <div key={s.story_id} className="border border-forest/15 bg-cream p-8">
                <p className="font-serif text-xl text-charcoal/85 leading-relaxed">"{s.body}"</p>
                <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-moss">{s.display_name}</p>
              </div>
            ))}
          </div>
        </Reveal>
      )}

      {/* YOU SHARED WE LISTENED */}
      <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="grid lg:grid-cols-12 gap-10">
          <SectionHeader eyebrow="A two-way relationship" title="You shared. We listened." className="lg:col-span-5" />
          <div className="lg:col-span-6 lg:col-start-7">
            {listened.length === 0 ? (
              <p className="text-lg text-charcoal/65 font-light leading-relaxed lg:pt-3">
                As members share their experiences, we'll publish the themes we heard and the decisions they informed—each with a date. This will not imply that every suggestion is adopted.
              </p>
            ) : (
              <div className="space-y-6">
                {listened.map((u) => (
                  <div key={u.id} className="border-l border-champagne pl-6 py-2">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-charcoal/45">{u.published_at}</p>
                    <p className="mt-2 font-serif text-2xl text-forest">{u.theme}</p>
                    <p className="mt-2 text-sm text-charcoal/70 font-light">{u.action}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Reveal>

      {/* JOURNAL */}
      <section className="bg-linen">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="flex items-end justify-between gap-4 mb-12">
            <SectionHeader eyebrow="Learn together" title="The Journal" />
            <Link to="/journal" className="link-underline text-[11px] uppercase tracking-[0.22em] text-forest whitespace-nowrap">View all</Link>
          </div>
          {articles.length === 0 ? (
            <p className="text-lg text-charcoal/65 max-w-2xl font-light leading-relaxed">
              Honest articles on skincare and formulation are on the way. Join the community to be the first to read them.
            </p>
          ) : (
            <div className="grid gap-8 md:grid-cols-3">
              {articles.slice(0, 3).map((a) => (
                <Link key={a.slug} to={`/journal/${a.slug}`} className="group bg-cream border border-forest/10 overflow-hidden hover:border-forest/40 transition-colors">
                  {a.image && <img src={a.image} alt={a.title} className="w-full aspect-[4/3] object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />}
                  <div className="p-7">
                    <h3 className="font-serif text-2xl text-forest">{a.title}</h3>
                    <p className="mt-2 text-sm text-charcoal/70 line-clamp-2 font-light">{a.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Reveal>
      </section>

      {/* FOUNDER NOTE */}
      <Reveal as="section" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
        <BrandLogo variant="mark" className="h-16 w-auto mx-auto" />
        <p className="eyebrow eyebrow-plain mt-10 justify-center">A note from ELA</p>
        <blockquote className="mt-8 font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-forest leading-[1.15] text-balance">
          "We built Rituals With You because good skincare is a shared journey. We listen to real experiences, and we let honest feedback inform our work."
        </blockquote>
        <div className="mt-10 mx-auto w-16 h-px bg-champagne" />
      </Reveal>

      {/* FAQ */}
      <section className="bg-linen">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-12">
          <SectionHeader eyebrow="Good to know" title="Questions, answered" className="lg:col-span-4" />
          <Accordion type="single" collapsible className="lg:col-span-7 lg:col-start-6 border-t border-forest/15">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-forest/15">
                <AccordionTrigger className="text-left font-serif text-2xl text-forest font-light hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-charcoal/70 text-base leading-relaxed font-light pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      {/* FINAL INVITATION */}
      <section className="relative bg-forest text-ivory overflow-hidden grain">
        <BrandLogo variant="mark" tone="light" className="pointer-events-none select-none absolute -right-24 -top-24 w-[560px] opacity-[0.06]" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40 text-center">
          <p className="eyebrow eyebrow-plain text-champagne justify-center">An open invitation</p>
          <h2 className="mt-8 font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight text-balance leading-[0.95]">
            You're invited to join Rituals With You
          </h2>
          <p className="mt-8 text-ivory/70 max-w-xl mx-auto font-light leading-relaxed">
            Discover the products, experience them when programmes open, and share your ritual with a community that listens.
          </p>
          <Link to="/join" className="btn btn-light mt-12">
            {BRAND.joinCta} <ArrowRight size={14} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
